/* eslint-disable react-hooks/set-state-in-effect -- debounced search resets local state when query scope changes */
import { useEffect, useRef, useState } from 'react';

import {
  fetchPortfolioSearchBeerIds,
  isPortfolioSearchActive,
} from '../utils/brewhousePortfolio/portfolioTypesenseSearch';

export interface UseBrewhousePortfolioSearchResult {
  beerIds: string[];
  loading: boolean;
  error: string | null;
}

/**
 * Debounced Typesense search for brewhouse pint portfolio (name + description).
 */
export function useBrewhousePortfolioSearch(
  searchQuery: string,
  portfolioBreweryIds: string[],
  debounceMs = 300,
): UseBrewhousePortfolioSearchResult {
  const [beerIds, setBeerIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isPortfolioSearchActive(searchQuery)) {
      setBeerIds([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!isMountedRef.current || cancelled) {
        return;
      }

      setLoading(true);
      setError(null);

      fetchPortfolioSearchBeerIds(searchQuery, portfolioBreweryIds)
        .then((result) => {
          if (cancelled || !isMountedRef.current) {
            return;
          }
          setBeerIds(result.beerIds);
          setError(result.error);
        })
        .finally(() => {
          if (!cancelled && isMountedRef.current) {
            setLoading(false);
          }
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [searchQuery, portfolioBreweryIds, debounceMs]);

  return { beerIds, loading, error };
}
